import {
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query';
import type { DeliveriesDeliveryFiltersResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { deliveryKeys, parseDelivery, type Delivery, type DeliveriesListParams, type DeliveryDetail } from './entity';

/**
 * Delivery reads. The Outbox is a live log — Ember re-runs `store.query` on
 * every filter change and appends on load-more — so the list uses
 * `staleTime: 0` and continuation-based `useInfiniteQuery`.
 */

const LIST_STALE_TIME = 0;
const FILTERS_STALE_TIME = 60_000;
const PAGE_SIZE = 50;

export interface DeliveriesPage {
  deliveries: readonly Delivery[];
  continuation: string;
}

/** @public */
export async function getDeliveriesPage(
  workspaceId: number,
  params: DeliveriesListParams,
  continuation: string,
  qc: QueryClient,
): Promise<DeliveriesPage> {
  const data = await hydraUiApi.deliveries.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: { ...params, size: PAGE_SIZE, continuation: isEmptyString(continuation) ? undefined : continuation },
    },
  });
  const deliveries = (data.deliveries ?? []).map(parseDelivery);
  // Sideload each row into its detail slot so a row-detail open resolves from cache.
  for (const d of deliveries) {
    qc.setQueryData(deliveryKeys.detail(workspaceId, d.id), d);
  }
  return { deliveries, continuation: data.meta?.continuation ?? '' };
}

interface UseDeliveriesOptions {
  params?: DeliveriesListParams;
  queryConfig?: { enabled?: boolean; refetchInterval?: number | false };
}

/**
 * Infinite, continuation-paginated deliveries list. Returns the flattened rows
 * plus load-more controls (`hasMore` / `fetchMore` / `isFetchingMore`) and
 * `lastUpdated`, mirroring the Ember controller's `fetchDeliveries` /
 * `fetchMore` / `lastUpdated`.
 *
 * @public
 */
export const useDeliveries = ({ params = {}, queryConfig }: UseDeliveriesOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const query = useInfiniteQuery({
    queryKey: deliveryKeys.list(workspaceId, params),
    queryFn: ({ pageParam }) => getDeliveriesPage(workspaceId, params, pageParam, qc),
    initialPageParam: '',
    getNextPageParam: (last) => (isEmptyString(last.continuation) ? undefined : last.continuation),
    staleTime: LIST_STALE_TIME,
    enabled: queryConfig?.enabled,
    refetchInterval: queryConfig?.refetchInterval,
  });

  const deliveries = query.data?.pages.flatMap((page) => page.deliveries) ?? [];

  return {
    ...query,
    deliveries,
    hasMore: query.hasNextPage,
    fetchMore: query.fetchNextPage,
    isFetchingMore: query.isFetchingNextPage,
    lastUpdated: query.dataUpdatedAt,
  };
};

/** @public */
export async function getDelivery(workspaceId: number, id: string): Promise<Delivery> {
  const data = await hydraUiApi.deliveries.get({
    params: { path: { environment_id: String(workspaceId), id } },
  });
  if (data.delivery == null) {
    throw new Error('delivery response did not include a delivery');
  }
  return parseDelivery(data.delivery);
}

/** @public */
export const getDeliveryQueryOptions = (workspaceId: number, id: string) =>
  queryOptions({
    queryKey: deliveryKeys.detail(workspaceId, id),
    queryFn: () => getDelivery(workspaceId, id),
    staleTime: LIST_STALE_TIME,
  });

/** @public */
export const useDelivery = ({
  id,
  queryConfig,
}: {
  id: string;
  queryConfig?: QueryConfig<typeof getDeliveryQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getDeliveryQueryOptions(workspaceId, id), ...queryConfig });
};

/**
 * The rendered delivery detail (`/delivery_details/:id`). The `edit` variant
 * (`/delivery_details/:id/edit`) returns the original source with unwrapped
 * tracking links, for drafts being edited.
 *
 * @public
 */
export async function getDeliveryDetailContent(workspaceId: number, id: string): Promise<DeliveryDetail> {
  const data = await hydraUiApi.deliveries.getDeliveryDetails({
    params: { path: { environment_id: String(workspaceId), id } },
  });
  if (data.delivery_detail == null) {
    throw new Error('delivery detail response did not include a delivery_detail');
  }
  return data.delivery_detail;
}

/** @public */
export async function getDeliveryDetailEdit(workspaceId: number, id: string): Promise<DeliveryDetail> {
  const data = await hydraUiApi.deliveries.getDeliveryDetailsEdit({
    params: { path: { environment_id: String(workspaceId), id } },
  });
  if (data.delivery_detail == null) {
    throw new Error('delivery detail (edit) response did not include a delivery_detail');
  }
  return data.delivery_detail;
}

/** @public */
export const useDeliveryDetailContent = ({ id }: { id: string }) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({
    queryKey: deliveryKeys.detailContent(workspaceId, id),
    queryFn: () => getDeliveryDetailContent(workspaceId, id),
    staleTime: LIST_STALE_TIME,
  });
};

/**
 * Non-suspending detail-content read for the expanded row. The row shows the
 * delivery's error messages and gates Resend on `hide_body`; a detail-fetch
 * failure must degrade gracefully (leave those absent) rather than tear down
 * the row, so this tolerates errors instead of suspending. `enabled: false`
 * keeps it dormant until the row expands.
 *
 * @public
 */
export const useDeliveryDetailContentOptional = ({ id, enabled = true }: { id: string; enabled?: boolean }) => {
  const workspaceId = requireWorkspaceId();
  const active = enabled && id !== '';
  return useQuery({
    queryKey: active ? deliveryKeys.detailContent(workspaceId, id) : ['delivery', 'detail-content', 'inactive'],
    queryFn: active ? () => getDeliveryDetailContent(workspaceId, id) : skipToken,
    staleTime: LIST_STALE_TIME,
    retry: false,
  });
};

/** @public */
export async function getDeliveryFilters(workspaceId: number): Promise<DeliveriesDeliveryFiltersResponse> {
  return hydraUiApi.deliveries.getFilters({
    params: { path: { environment_id: String(workspaceId) } },
  });
}

/** @public */
export const useDeliveryFilters = () => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({
    queryKey: deliveryKeys.filters(workspaceId),
    queryFn: () => getDeliveryFilters(workspaceId),
    staleTime: FILTERS_STALE_TIME,
  });
};
